import React, { useEffect, useState } from 'react';
import { supabase, usuarioDelToken } from '../lib/supabase';
import { leerEnlaceDeAcceso, quitarHashDeLaUrl } from '../lib/enlaceAcceso';
import AccessLinkScreen from '../pages/access-link';

// Handles the access links from provision-client.ts (invite / recovery), which arrive with a
// session in the URL hash. The Supabase client no longer reads sessions from the URL
// (detectSessionInUrl: false, see src/lib/supabase.js): this gate is the ONLY place that can,
// and only after the person clicks Continue on a screen that shows whose account it is.
//
// Whose account it is comes from Supabase (usuarioDelToken), never from the token's payload:
// anyone can write an email there, and setSession does not check it (with an expired token it
// only uses the refresh token). A link with a forged token naming the victim's email plus the
// attacker's refresh token would otherwise show the victim's own address. After Continue, the
// adopted session must belong to the account that was shown, or it is signed out again.
//
// It wraps every route so the link works wherever Supabase redirects (Site URL or
// ITSM_REDIRECT_TO), and it renders in place instead of navigating: ProtectedRoute would send a
// visitor without a session to /login-screen, and that redirect drops the hash.
//
// The tokens leave the address bar as soon as they are read and live only in this component's
// state. Any other session pasted into a URL is dropped without signing anyone in.
const AccessLinkGate = ({ children }) => {
  // Read once, on the first render: the hash is cleared right after.
  const [enlace, setEnlace] = useState(() =>
    typeof window === 'undefined' ? null : leerEnlaceDeAcceso(window.location.hash)
  );
  // The account the link opens, as Supabase reports it: { id, email }.
  const [cuenta, setCuenta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const esEnlace = enlace?.tipo === 'invite' || enlace?.tipo === 'recovery';

  const verificar = async () => {
    setError('');
    try {
      const usuario = await usuarioDelToken(enlace.access_token);
      if (usuario) setCuenta(usuario);
      else setEnlace({ tipo: 'invalido' });
    } catch {
      setError('Could not reach the server to check this link.');
    }
  };

  useEffect(() => {
    if (enlace) quitarHashDeLaUrl();
    if (esEnlace) verificar();
    // Only on mount: later state changes never put tokens back in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!enlace || enlace.tipo === 'ignorado') return children;

  const continuar = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: errSesion } = await supabase.auth.setSession({
        access_token: enlace.access_token,
        refresh_token: enlace.refresh_token,
      });
      if (errSesion) {
        setEnlace({ tipo: 'invalido' });
        return;
      }
      if (data?.user?.id !== cuenta?.id) {
        // setSession opened a different account than the one confirmed (it can skip the
        // access token and use only the refresh token): undo it locally and stop.
        await supabase.auth.signOut({ scope: 'local' });
        setEnlace({ tipo: 'invalido' });
        return;
      }
      // setSession already notified AuthContext (SIGNED_IN), so the routes render signed in
      // and ProtectedRoute shows "Set your password" for the provisioned account.
      setEnlace(null);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessLinkScreen
      tipo={enlace.tipo}
      email={cuenta?.email}
      verificando={esEnlace && !cuenta}
      loading={loading}
      error={error}
      onContinue={continuar}
      onRetry={verificar}
      onCancel={() => setEnlace(null)}
    />
  );
};

export default AccessLinkGate;
